import type {
  Alert,
  Phone,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useSaveAlert } from "@tandemdiabetes/sugarmate-api-react-client"
import { useMemo } from "react"
import { useAlerts } from "./useAlerts"
import { noticeError } from "src/utils"

export function useUpdateAlert(
  primaryPhone: Phone | undefined,
  sugarmateAlert: Alert | undefined,
) {
  const saveAlert = useSaveAlert()
  const {
    mutate,
    data: alerts,
  } = useAlerts()

  return useMemo(
    () => !saveAlert || !sugarmateAlert?.id
      ? undefined
      : (async (updatedAlert: Partial<Omit<Alert, "id">>) => {
        try {
          let phoneIds = updatedAlert.phone_ids ?? sugarmateAlert?.phone_ids

          // automatically assign the primary phone if the alert has no phone ids
          if (phoneIds?.length === 0 && primaryPhone?.id) {
            phoneIds = [primaryPhone.id]
          }

          const response = await saveAlert({
            id: sugarmateAlert.id,
            ...updatedAlert,
            phone_ids: phoneIds,
          })

          if (alerts) {
            const responseAlert = response.data.alert

            const updatedList = alerts.map((cachedAlert) => {
              if (cachedAlert.id === responseAlert.id) {
                return responseAlert
              }

              return cachedAlert
            })

            void mutate(updatedList)
          } else {
            void mutate()
          }

          return response
        } catch (e) {
          noticeError(
            e,
            "UpdateAlert",
            {
              alert: {
                id: sugarmateAlert.id,
                ...updatedAlert,
              },
            },
          )

          throw e
        }
      }),
    [
      saveAlert,
      alerts,
      mutate,
      sugarmateAlert?.phone_ids,
      sugarmateAlert?.id,
      primaryPhone?.id,
    ],
  )
}
